import { Alert, FormGroup, FormParagraph, FormSection } from '@ksyos/design-system';
import { capitalize } from 'lodash';
import type { RespiAllerGradingDataV1 } from '@common/validation/customControls/allergology/respiAllerGrading/v1/respiAllerGradingDataV1';
import type { RespiAllerReferralCustomControlData } from '@common/validation/customControls/allergology/respiAllerReferral/v1/respiAllerReferralSchemaV1';
import { CustomControlCode, ProcessActivityStatus, RespiAllerValueType } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { AbstractSyntaxTreeBuilder } from '@common/utils/abstractSyntaxTreeBuilder';
import { getCustomControlData } from '@common/utils/processUtil';
import { ExportPdfThumbnail } from '@frontend/components/ExportPdfThumbnail';
import { ProcessContext } from '@frontend/components/pages/episodeCategory/carePath/process/processContext';
import { AbstractSyntaxTreeReactRenderer } from '@frontend/utils/abstractSyntaxTreeReactRenderer';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';

type RespiAllerGradingProps = {
    status?: ProcessActivityStatus;
};

export function RespiAllerGradingContent(props: Readonly<RespiAllerGradingProps>) {
    const { status } = props;
    const { process } = useDefinedContext(ProcessContext);

    const referralCustomControlData = getCustomControlData<RespiAllerReferralCustomControlData>(
        process,
        CustomControlCode.RespiAllerReferral,
        [ProcessActivityStatus.complete]
    );

    const gradingCustomControlData = getCustomControlData<RespiAllerGradingDataV1>(
        process,
        CustomControlCode.RespiAllerGrading,
        [ProcessActivityStatus.complete]
    );

    const referralQuestion = referralCustomControlData?.referralQuestion;

    const translate = getLanguage();

    return (
        <>
            {status === ProcessActivityStatus.complete && (
                <>
                    {gradingCustomControlData?.conclusie && (
                        <FormSection
                            isBorderless
                            title={translate.allergology.conclusion}
                        >
                            <FormGroup label={capitalize(RespiAllerValueType.conclusie)}>
                                {formatText(gradingCustomControlData?.conclusie)}
                            </FormGroup>
                        </FormSection>
                    )}
                    {(gradingCustomControlData?.individuele_aanbevelingen ||
                        gradingCustomControlData?.medicamenteus_advies ||
                        gradingCustomControlData?.opmerkingen_voor_huisarts) && (
                        <FormSection
                            isBorderless
                            title={translate.allergology.advice}
                        >
                            {gradingCustomControlData?.individuele_aanbevelingen && (
                                <FormGroup label={capitalize(RespiAllerValueType.individueleAanbevelingen)}>
                                    {formatText(gradingCustomControlData?.individuele_aanbevelingen)}
                                </FormGroup>
                            )}
                            {gradingCustomControlData?.medicamenteus_advies && (
                                <FormGroup label={capitalize(RespiAllerValueType.medicamenteusAdvies)}>
                                    {formatText(gradingCustomControlData?.medicamenteus_advies)}
                                </FormGroup>
                            )}
                            {gradingCustomControlData?.opmerkingen_voor_huisarts && (
                                <FormGroup label={capitalize(RespiAllerValueType.opmerkingenVoorHuisarts)}>
                                    {formatText(gradingCustomControlData?.opmerkingen_voor_huisarts)}
                                </FormGroup>
                            )}
                        </FormSection>
                    )}

                    <FormSection
                        isBorderless
                        title={translate.allergology.completeAssessment}
                    >
                        <Alert appearance="info">{translate.allergology.reportGraderAlert}</Alert>
                        <FormGroup label={translate.allergology.reportGrader}>
                            <ExportPdfThumbnail
                                filename="Verslag medisch specialist.pdf"
                                url={`/api/exportPdf/processes/${process.id}?include=RespiAllerGrading`}
                            />
                        </FormGroup>
                    </FormSection>
                </>
            )}
            {status !== ProcessActivityStatus.complete && (
                <>
                    <Alert appearance="info">{translate.allergology.waitingForGradingResult}</Alert>
                    {referralQuestion && (
                        <FormGroup label={translate.allergology.questionFromGP}>
                            <FormParagraph>{referralQuestion}</FormParagraph>
                        </FormGroup>
                    )}
                </>
            )}
        </>
    );

    function formatText(text: string) {
        const renderer = new AbstractSyntaxTreeReactRenderer();
        const builder = new AbstractSyntaxTreeBuilder(renderer);

        return builder.render(text);
    }
}
